import { deleteApp, initializeApp } from "firebase/app";
import { inMemoryPersistence, initializeAuth, signInWithCustomToken, signOut, type Auth } from "firebase/auth";

/**
 * AI店長コンソールのログインでイチニンマエに入る(#357)。
 * sakama-context の docs/ai-tencho/module-contract.md「1. 認証の委譲」の手順1・6〜10。
 *
 * 1. 押されたボタンで、コンソールの /sso?module=ichininmae&state=<乱数> をポップアップで開く
 * 2. コンソールが postMessage で返したカスタムトークンを、送り元のオリジン・ポップアップ・state を確かめてから受け取る
 * 3. 既定とは別に初期化したコンソールの Firebase のアプリでサインインし、コンソールの ID トークンを取る
 * 4. その ID トークンで Functions の aiTencho の POST /sso を呼び、イチニンマエのカスタムトークンに引き換える
 * 5. 引き換えたカスタムトークンでイチニンマエにサインインする。コンソールのアプリはサインアウトして消す
 *
 * トークンは URL に載せない。コンソールのアプリのログインは保存しない(inMemoryPersistence)。
 * 入ったあとはイチニンマエの自分の役割で動く。画面の URL は変えないので、開いていたページへそのまま進む。
 */

export const SSO_MESSAGE_TYPE = "ai-tencho:sso";
const MODULE = "ichininmae";
/** ポップアップの中でコンソールにログインする人もいるので、長めに待つ */
const TIMEOUT_MS = 10 * 60 * 1000;

export type SsoErrorCode =
  | "popup_blocked"
  | "popup_closed"
  | "timeout"
  | "invitation_required"
  | "role_not_supported"
  | "exchange_failed";

/** AI店長のログインで入れなかった理由。t() の原文なので、そのまま辞書(i18n.zh.ts)のキーになる */
export const SSO_ERROR_LABEL: Record<SsoErrorCode, string> = {
  popup_blocked: "ポップアップが止められました。このサイトのポップアップを許可してから、もう一度お試しください。",
  popup_closed: "AI店長の画面が閉じられました。もう一度お試しください。",
  timeout: "時間切れになりました。もう一度お試しください。",
  invitation_required: "招待が必要です。AI店長のスタッフ登録が、イチニンマエの利用者とまだ結ばれていません。管理者に連絡してください。",
  role_not_supported: "AI店長のこの役割では、イチニンマエの画面に入れません。AI店長のホームから閲覧してください。",
  exchange_failed: "AI店長のログインを引き換えられませんでした。時間をおいて、もう一度お試しください。",
};

export class SsoError extends Error {
  constructor(readonly code: SsoErrorCode) {
    super(code);
  }
}

/** コンソールの Firebase のウェブアプリの設定の項目。値は web/.env の VITE_AI_TENCHO_FIREBASE_<項目の大文字> に置く */
const FIREBASE_FIELDS = ["apiKey", "authDomain", "projectId", "appId"] as const;
type FirebaseWebConfig = Record<(typeof FIREBASE_FIELDS)[number], string>;
export type ConsoleConfig = { url: string; firebase: FirebaseWebConfig };

/** apiKey → VITE_AI_TENCHO_FIREBASE_API_KEY */
export function envNameOf(field: string): string {
  return `VITE_AI_TENCHO_FIREBASE_${field.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`;
}

/**
 * コンソールの URL と Firebase のウェブアプリの設定(公開前提の値)。web/.env の VITE_AI_TENCHO_* から取る。
 * 1つでも無ければ null で、ログイン画面にボタンを出さない。エミュレータでは使わない(引き換えの入口が本番のため)。
 */
export function consoleConfig(env: Record<string, string | undefined> = import.meta.env): ConsoleConfig | null {
  if (env.VITE_USE_EMULATORS === "1") return null;
  const url = env.VITE_AI_TENCHO_CONSOLE_URL;
  const entries = FIREBASE_FIELDS.map((field) => [field, env[envNameOf(field)]] as const);
  if (!url || entries.some(([, v]) => !v)) return null;
  return { url, firebase: Object.fromEntries(entries) as FirebaseWebConfig };
}

/** 引き換えの入口(functions/ai-tencho.mjs の aiTencho) */
export function ssoEndpoint(projectId: string): string {
  return `https://asia-northeast1-${projectId}.cloudfunctions.net/aiTencho/sso`;
}

function randomState(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 受け取ってよいメッセージか。送り元のオリジン、開いたポップアップ、state のすべてが合うときだけ */
export function acceptSsoMessage(
  event: Pick<MessageEvent, "origin" | "source" | "data">,
  expected: { origin: string; popup: unknown; state: string },
): string | null {
  if (event.origin !== expected.origin || event.source !== expected.popup) return null;
  const d = event.data as { type?: unknown; state?: unknown; custom_token?: unknown } | null;
  if (!d || d.type !== SSO_MESSAGE_TYPE || d.state !== expected.state) return null;
  return typeof d.custom_token === "string" && d.custom_token !== "" ? d.custom_token : null;
}

/** POST /sso の応答の状態から、画面に出す理由を決める */
export function exchangeErrorOf(status: number): SsoErrorCode {
  if (status === 404) return "invitation_required";
  if (status === 403) return "role_not_supported";
  return "exchange_failed";
}

/** コンソールの /sso をポップアップで開き、カスタムトークンを待つ。ボタンを押した処理の中で、await より先に呼ぶ */
function receiveConsoleToken(consoleUrl: string): Promise<string> {
  const origin = new URL(consoleUrl).origin;
  const state = randomState();
  const url = new URL("/sso", origin);
  url.searchParams.set("module", MODULE);
  url.searchParams.set("state", state);
  // ブラウザは、押されたとき以外に開いたポップアップを止める
  const popup = window.open(url.toString(), "ai-tencho-sso", "popup,width=480,height=720");
  if (!popup) return Promise.reject(new SsoError("popup_blocked"));

  return new Promise<string>((resolve, reject) => {
    let giveUp: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      clearInterval(closedTimer);
      clearTimeout(timeout);
      if (giveUp) clearTimeout(giveUp);
    };
    const onMessage = (event: MessageEvent) => {
      const t = acceptSsoMessage(event, { origin, popup, state });
      if (!t) return;
      cleanup();
      resolve(t);
    };
    // 受け取る前に閉じられたら諦める。コンソールは送ってから閉じるので、閉じた後も少しだけ待つ
    const closedTimer = setInterval(() => {
      if (popup.closed && !giveUp) {
        giveUp = setTimeout(() => {
          cleanup();
          reject(new SsoError("popup_closed"));
        }, 1000);
      }
    }, 500);
    const timeout = setTimeout(() => {
      cleanup();
      popup.close();
      reject(new SsoError("timeout"));
    }, TIMEOUT_MS);
    window.addEventListener("message", onMessage);
  });
}

/** コンソールのカスタムトークンで、コンソールの ID トークンを取る。コンソールのアプリは使い終わったら消す */
async function consoleIdToken(config: FirebaseWebConfig, customToken: string): Promise<string> {
  const app = initializeApp(config, `ai-tencho-console-${randomState()}`);
  const consoleAuth = initializeAuth(app, { persistence: inMemoryPersistence });
  try {
    const cred = await signInWithCustomToken(consoleAuth, customToken);
    return await cred.user.getIdToken();
  } finally {
    await signOut(consoleAuth).catch(() => {});
    await deleteApp(app).catch(() => {});
  }
}

/** projectId はイチニンマエの Firebase のプロジェクト(引き換えの入口の場所) */
export async function signInWithConsole(auth: Auth, config: ConsoleConfig, projectId: string): Promise<void> {
  const consoleToken = await receiveConsoleToken(config.url);
  const idToken = await consoleIdToken(config.firebase, consoleToken);
  let res: Response;
  try {
    res = await fetch(ssoEndpoint(projectId), {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
      cache: "no-store",
    });
  } catch {
    throw new SsoError("exchange_failed");
  }
  if (!res.ok) throw new SsoError(exchangeErrorOf(res.status));
  const body = (await res.json().catch(() => ({}))) as { custom_token?: unknown };
  if (typeof body.custom_token !== "string" || !body.custom_token) throw new SsoError("exchange_failed");
  await signInWithCustomToken(auth, body.custom_token);
}
